import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  TrendingUp, 
  BarChart3, 
  Gamepad2, 
  Target,
  Monitor,
  Smartphone,
  Users,
  Palette,
  Share2,
  Tv,
  Wallet,
  ShoppingCart,
  Plane,
  CreditCard,
  RefreshCcw
} from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden grid-bg" data-testid="hero-section">
      {/* Background Glow Effects */}
      <div className="hero-glow -top-40 -left-40 animate-pulse-glow" />
      <div className="hero-glow bottom-0 right-0 opacity-50" style={{ background: 'radial-gradient(circle, rgba(112, 0, 255, 0.15) 0%, transparent 70%)' }} />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="max-w-4xl">
          <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-white mb-6 animate-in">
            Performance Marketing
            <br />
            <span className="text-neon-green">Engineered for ROAS.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed animate-in delay-100">
            We help gaming, app, and digital-first brands scale profitably through data-backed user acquisition, creative intelligence, and precision media buying.
          </p>
          
          <div className="flex flex-wrap gap-4 animate-in delay-200">
            <Link to="/contact" className="btn-primary flex items-center gap-2" data-testid="hero-cta-scale">
              Scale My Growth
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary flex items-center gap-2" data-testid="hero-cta-call">
              Book a Strategy Call
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhoWeAre = () => {
  const targets = [
    { icon: Smartphone, label: 'Mobile-first businesses' },
    { icon: Gamepad2, label: 'Gaming studios' },
    { icon: Monitor, label: 'App publishers' },
    { icon: ShoppingCart, label: 'D2C & digital brands' },
  ];

  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="who-we-are-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              Who We Are
            </h2>
            <p className="text-xl text-neon-green font-medium mb-6">
              At The ROAS Labs, performance isn't a metric — it's the foundation.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              We are a growth-focused performance marketing agency built for businesses that demand measurable results and profitable scale.
            </p>
            <p className="text-gray-500 text-base">
              Every campaign we build is engineered around one core principle: <span className="text-white font-semibold">Profitable Scale.</span>
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {targets.map((item, index) => (
              <div 
                key={index}
                className="glass p-6 rounded-xl card-hover"
                data-testid={`target-card-${index}`}
              >
                <item.icon className="w-8 h-8 text-neon-green mb-4" />
                <p className="text-white font-medium">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatMakesUsDifferent = () => {
  const differentiators = [
    {
      icon: TrendingUp,
      title: 'ROAS-First Approach',
      description: "We don't optimize for clicks. We optimize for revenue, retention, and LTV.",
      color: 'text-neon-green'
    },
    {
      icon: BarChart3,
      title: 'Deep Performance Analytics',
      description: 'Advanced cohort tracking, attribution modeling, and funnel diagnostics.',
      color: 'text-neon-cyan'
    },
    {
      icon: Gamepad2,
      title: 'Gaming & App DNA',
      description: 'We understand CPI, ARPU, LTV curves, retention metrics, and monetization cycles.',
      color: 'text-neon-purple'
    },
    {
      icon: Target,
      title: 'Full-Funnel Strategy',
      description: 'From first impression to repeat purchase.',
      color: 'text-neon-green'
    },
  ];

  return (
    <section className="py-20 md:py-32 grid-bg" data-testid="differentiators-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white text-center mb-16">
          What Makes Us Different
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentiators.map((item, index) => (
            <div 
              key={index}
              className="bg-surface border border-white/5 rounded-xl p-8 card-hover group"
              data-testid={`differentiator-card-${index}`}
            >
              <div className={`w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center mb-6 group-hover:neon-border transition-all`}>
                <item.icon className={`w-7 h-7 ${item.color}`} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CoreServices = () => {
  const services = [
    { icon: Monitor, name: 'Display Advertising' },
    { icon: Smartphone, name: 'Mobile Marketing' },
    { icon: Users, name: 'Lead Generation' },
    { icon: Palette, name: 'Creative Solutions' },
    { icon: Share2, name: 'Social Media Marketing' },
  ];

  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
            Our Core Services
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Comprehensive performance marketing solutions designed to drive measurable growth.
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {services.map((service, index) => (
            <div 
              key={index}
              className="glass p-6 rounded-xl text-center card-hover group"
              data-testid={`service-card-${index}`}
            >
              <service.icon className="w-10 h-10 text-neon-green mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <p className="text-white font-medium text-sm">{service.name}</p>
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="explore-services-btn">
            Explore Our Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Industries = () => {
  const industries = [
    { icon: Gamepad2, name: 'Mobile Gaming' },
    { icon: Tv, name: 'OTT Platforms' },
    { icon: Wallet, name: 'Fintech Apps' },
    { icon: ShoppingCart, name: 'E-commerce' },
    { icon: RefreshCcw, name: 'Subscription Businesses' },
    { icon: Plane, name: 'Travel & Lifestyle Apps' },
  ];

  return (
    <section className="py-20 md:py-32 grid-bg" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white text-center mb-16">
          Industries We Serve
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {industries.map((industry, index) => (
            <div 
              key={index}
              className="bg-surface/50 border border-white/5 rounded-xl p-6 text-center card-hover"
              data-testid={`industry-card-${index}`}
            >
              <industry.icon className="w-8 h-8 text-neon-cyan mx-auto mb-3" />
              <p className="text-gray-300 text-sm font-medium">{industry.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClosingCTA = () => {
  return (
    <section className="py-20 md:py-32 bg-surface relative overflow-hidden" data-testid="closing-cta-section">
      <div className="hero-glow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
          Ready to Scale Beyond <span className="text-neon-green">Vanity Metrics?</span>
        </h2>
        <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
          At The ROAS Labs, we build growth systems — not just campaigns.
        </p>
        <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="closing-cta-btn">
          Start Scaling Profitably
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />
      <WhoWeAre />
      <WhatMakesUsDifferent />
      <CoreServices />
      <Industries />
      <ClosingCTA />
    </div>
  );
};

export default Home;
